import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useReactivateSubscriptionMutation } from "src/api/services/patientSubscriptionApi";
import { Task } from "src/api/types";
import BasicModal from "./BasicModal";
import RateModal from "./RateModal";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import MessageModal from "src/shared/components/MessageModal";

type ModalTypes = {
  open: boolean;
  onClose: (o: boolean) => void;

  task: Task;
};

const TaskModal = ({ open, onClose, task }: ModalTypes) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const component = `task-modal.${task.task_type.toLowerCase()}`;
  const [reactivateSubscription] = useReactivateSubscriptionMutation();
  const errorLog = useSentryLogger("error");

  const modalTitle = useMemo(() => t(`${component}.title`), [component, t]);

  const modalText = useMemo(() => {
    if (
      ["RATE_ORDER", "RATE_EXPERIENCE", "DISCOUNT_OFFER"].includes(
        task.task_type,
      )
    ) {
      return null;
    }

    return t(`${component}.text`);
  }, [component, t, task.task_type]);

  const modalButton = useMemo(() => {
    if (["DISCOUNT_OFFER"].includes(task.task_type)) {
      return null;
    }

    return t(`${component}.buttonText`);
  }, [component, t, task.task_type]);

  const handleNavigation = useCallback(() => {
    if (!task?.task_link) return;

    navigate(`${task.task_link}`);
  }, [navigate, task]);

  const handleSubscriptionReactivation = useCallback(async () => {
    try {
      const reactivation = await reactivateSubscription(
        task.resource_id,
      ).unwrap();

      if ("refill_url" in reactivation) {
        navigate(reactivation.refill_url);
        return;
      }

      return reactivation;
    } catch (err) {
      errorLog(
        `Oops, looks like there an issue trying to reactivate this membership ${task.resource_id}`,
        err,
      );
    }
  }, [errorLog, navigate, reactivateSubscription, task.resource_id]);

  const handleClick = useCallback(() => {
    if (task.task_type === "REACTIVATE_MEMBERSHIP") {
      return handleSubscriptionReactivation();
    }

    return handleNavigation();
  }, [handleNavigation, handleSubscriptionReactivation, task.task_type]);

  if (["RATE_ORDER", "RATE_EXPERIENCE"].includes(task.task_type)) {
    return (
      <RateModal
        text={modalText}
        title={modalTitle}
        open={open}
        onClose={onClose}
        task={task}
      />
    );
  }

  if (["DISCOUNT_OFFER", "WL_OPTIONS"].includes(task.task_type)) {
    return (
      <MessageModal
        title={modalTitle}
        onClose={onClose}
        open={open}
        task={task}
      />
    );
  }

  return (
    <BasicModal
      buttonText={modalButton}
      text={modalText}
      title={modalTitle}
      open={open}
      onClose={onClose}
      handleClick={handleClick}
    />
  );
};

export default TaskModal;
